import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { deployments, revenue, type RevenueGranularity } from "@/api/endpoints";
import { Money } from "@/components/shared/Money";
import { Spinner } from "@/components/shared/Spinner";
import { useToast } from "@/components/shared/Toast";
import { useAuthStore } from "@/store/authStore";
import { AddDevicesDialog } from "./AddDevicesDialog";
import { StackedTotalTooltip } from "./ChartTooltip";
import { defaultWindow, WindowPicker, type Window } from "./WindowPicker";

const REVENUE_REFETCH_MS = 60 * 60 * 1000;

const fmtCompact = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

function autoGranularity(start: string, end: string): RevenueGranularity {
  const days = Math.round(
    (new Date(end).getTime() - new Date(start).getTime()) / (24 * 60 * 60 * 1000),
  );
  if (days <= 35) return "daily";
  if (days <= 180) return "weekly";
  return "monthly";
}

function formatPeriod(p: string, gran: RevenueGranularity): string {
  if (gran === "daily") return p.slice(5);
  if (gran === "weekly") return `wk ${p.slice(5)}`;
  return p.slice(0, 7);
}

export function DeploymentRevenueDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const initialWindow: Window = useMemo(() => {
    const start = searchParams.get("start");
    const end = searchParams.get("end");
    return start && end ? { start, end } : defaultWindow();
  }, [searchParams]);

  const [window, setWindow] = useState<Window>(initialWindow);
  const [granChoice, setGranChoice] = useState<RevenueGranularity | "auto">("daily");
  const tempoId = id!;

  const nav = useNavigate();
  const qc = useQueryClient();
  const showToast = useToast((s) => s.show);
  const me = useAuthStore((s) => s.user);
  const canEdit = me?.role === "admin";

  const [addOpen, setAddOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  const invalidateAfterMembershipChange = () => {
    qc.invalidateQueries({ queryKey: ["revenue", "deployment", tempoId] });
    qc.invalidateQueries({ queryKey: ["revenue", "deployment"] });
    qc.invalidateQueries({ queryKey: ["inventory"] });
  };

  const removeOne = useMutation({
    mutationFn: (deviceId: number) => deployments.unassign(tempoId, deviceId),
    onSuccess: (res) => {
      invalidateAfterMembershipChange();
      if (res.failed.length === 0) showToast("Device removed from deployment.", "success");
      else showToast(res.failed[0].error || "remove failed", "error");
    },
    onError: (err) => showToast(err instanceof Error ? err.message : "remove failed", "error"),
  });

  const clearAll = useMutation({
    mutationFn: () => deployments.clearAll(tempoId),
    onSuccess: (res) => {
      invalidateAfterMembershipChange();
      const failedN = res.failed.length;
      const okN = res.succeeded.length;
      if (failedN === 0) {
        showToast(
          `Cleared ${okN} device${okN === 1 ? "" : "s"} from deployment.`,
          "success",
        );
        setConfirmClear(false);
        // No devices left here — send the user back to the index.
        nav("/revenue");
      } else {
        showToast(`Cleared ${okN}; ${failedN} failed.`, "error");
        setConfirmClear(false);
      }
    },
    onError: (err) => showToast(err instanceof Error ? err.message : "clear failed", "error"),
  });

  const detail = useQuery({
    queryKey: ["revenue", "deployment", tempoId, window.start, window.end],
    queryFn: () => revenue.deploymentDetail(tempoId, window.start, window.end),
    refetchInterval: REVENUE_REFETCH_MS,
    // Keep showing the previous window's data while a new fetch runs. Without
    // this, every preset click flashes the page to a spinner — which also
    // unmounts the WindowPicker mid-click, making the buttons feel unclickable.
    placeholderData: keepPreviousData,
  });

  const granularity: RevenueGranularity =
    granChoice === "auto" ? autoGranularity(window.start, window.end) : granChoice;

  const timeseries = useQuery({
    queryKey: ["revenue", "deployment", tempoId, "timeseries", granularity, window.start, window.end],
    queryFn: () =>
      revenue.deploymentTimeseries(tempoId, granularity, window.start, window.end),
    enabled: !!detail.data,
    retry: false,
    refetchInterval: REVENUE_REFETCH_MS,
    placeholderData: keepPreviousData,
  });

  if (detail.isLoading) return <Spinner />;
  if (detail.error)
    return <div className="text-red-700">{(detail.error as Error).message}</div>;
  if (!detail.data) return null;
  const d = detail.data;

  const realized = Number(d.realized_revenue);
  const projectedTotal = timeseries.data ? Number(timeseries.data.projected_total) : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-baseline gap-3">
        <Link to="/revenue" className="text-sm text-slate-500 hover:underline">
          ← Revenue
        </Link>
        <h1 className="text-xl font-semibold">{d.name}</h1>
        {d.gpu_type_name && (
          <span className="text-sm text-slate-500">
            {d.num_nodes}× {d.gpus_per_node} {d.gpu_type_name}
          </span>
        )}
        {d.financing_type && (
          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
            {d.financing_type}
          </span>
        )}
        {d.tempo_url && (
          <a
            href={d.tempo_url}
            target="_blank"
            rel="noreferrer"
            className="ml-auto text-sm text-brand-700 hover:underline"
          >
            Open in Tempo ↗
          </a>
        )}
      </div>

      <div className="flex justify-end">
        <WindowPicker value={window} onChange={setWindow} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat label="Devices assigned">{d.devices.length}</Stat>
        <Stat label="Realized">
          <Money value={d.realized_revenue} />
        </Stat>
        <Stat label="Projected (Tempo sim)">
          {timeseries.isLoading ? (
            <Spinner label="" />
          ) : timeseries.data && timeseries.data.projection_available ? (
            <Money value={timeseries.data.projected_total} />
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </Stat>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 dark:bg-slate-900 dark:border-slate-800">
        <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
          <div className="flex items-baseline gap-3">
            <h2 className="text-sm font-medium">Realized vs. projected</h2>
            {(timeseries.isFetching || detail.isFetching) && (
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-brand-500" />
                Updating…
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs">
            <GranBtn cur={granChoice} k="auto" set={setGranChoice}>
              Auto
            </GranBtn>
            <GranBtn cur={granChoice} k="daily" set={setGranChoice}>
              Daily
            </GranBtn>
            <GranBtn cur={granChoice} k="weekly" set={setGranChoice}>
              Weekly
            </GranBtn>
            <GranBtn cur={granChoice} k="monthly" set={setGranChoice}>
              Monthly
            </GranBtn>
          </div>
        </div>
        <div className="h-72">
          {timeseries.isLoading ? (
            <Spinner />
          ) : timeseries.error ? (
            <div className="text-sm text-red-700">
              Couldn't load timeseries: {(timeseries.error as Error).message}
            </div>
          ) : timeseries.data && timeseries.data.periods.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={timeseries.data.periods.map((p, i) => ({
                  period: formatPeriod(p, granularity),
                  Realized: Number(timeseries.data!.realized[i] ?? 0),
                  Projected: Number(timeseries.data!.projected[i] ?? 0),
                }))}
                barCategoryGap="20%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="period" fontSize={11} interval="preserveStartEnd" />
                <YAxis fontSize={11} tickFormatter={(v) => `$${fmtCompact.format(v)}`} />
                <Tooltip content={<StackedTotalTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Realized" fill="#2563eb" isAnimationActive={false} />
                <Bar dataKey="Projected" fill="#a78bfa" isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-sm text-slate-500">No data in this window.</div>
          )}
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Realized comes from this deployment's devices' actual revenue events. Projected comes
          from Tempo's deployment-preview simulation (monthly), proportionally redistributed to
          the chosen granularity so periods are directly comparable.
          {timeseries.data && !timeseries.data.projection_available && (
            <>
              {" "}
              Projection unavailable: {timeseries.data.projection_unavailable_reason ?? "unknown"}.
            </>
          )}
        </p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-800">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2 dark:border-slate-800">
          <h2 className="text-sm font-medium">
            Devices in this deployment ({d.devices.length})
          </h2>
          {canEdit && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAddOpen(true)}
                className="rounded-md bg-brand-500 px-3 py-1 text-xs text-white hover:bg-brand-700"
              >
                Add devices…
              </button>
              {d.devices.length > 0 && (
                <button
                  onClick={() => setConfirmClear(true)}
                  className="rounded-md border border-red-300 px-3 py-1 text-xs text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/40"
                >
                  Clear all
                </button>
              )}
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
              <tr>
                <th className="px-3 py-2 text-left">Hostname</th>
                <th className="px-3 py-2 text-left">Tenant</th>
                <th className="px-3 py-2 text-left">GPU type</th>
                <th className="px-3 py-2 text-right">Realized</th>
                <th className="px-3 py-2 text-left">Last earned</th>
                {canEdit && <th className="px-3 py-2 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {d.devices.length === 0 && (
                <tr>
                  <td colSpan={canEdit ? 6 : 5} className="px-3 py-6 text-center text-slate-400">
                    No devices assigned.{" "}
                    {canEdit ? (
                      <button
                        onClick={() => setAddOpen(true)}
                        className="text-brand-700 hover:underline"
                      >
                        Add some now.
                      </button>
                    ) : (
                      "Ask an admin to assign hosts."
                    )}
                  </td>
                </tr>
              )}
              {d.devices.map((dev) => (
                <tr key={dev.netbox_device_id} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-3 py-2 font-medium">
                    <Link to={`/inventory/${dev.netbox_device_id}`} className="hover:underline">
                      {dev.name}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{dev.tenant ?? <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2">{dev.gpu_type ?? <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2 text-right">
                    <Money value={dev.realized_revenue} muted={Number(dev.realized_revenue) === 0} />
                  </td>
                  <td className="px-3 py-2 text-slate-500">{dev.last_earning_date ?? "—"}</td>
                  {canEdit && (
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => removeOne.mutate(dev.netbox_device_id)}
                        disabled={removeOne.isPending}
                        className="text-xs text-red-700 hover:underline disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {addOpen && (
        <AddDevicesDialog
          tempoId={tempoId}
          deploymentName={d.name}
          excludeDeviceIds={d.devices.map((dev) => dev.netbox_device_id)}
          onClose={() => setAddOpen(false)}
        />
      )}

      {confirmClear && (
        <ConfirmClear
          deploymentName={d.name}
          count={d.devices.length}
          pending={clearAll.isPending}
          onConfirm={() => clearAll.mutate()}
          onCancel={() => setConfirmClear(false)}
        />
      )}

      {projectedTotal !== null && projectedTotal > 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 dark:bg-slate-800/50 dark:border-slate-800 dark:text-slate-300">
          Variance: realized <Money value={d.realized_revenue} /> vs. projected{" "}
          <Money value={projectedTotal} /> →{" "}
          <strong>
            {(((realized - projectedTotal) / projectedTotal) * 100).toFixed(1)}%
          </strong>{" "}
          of projection.
        </div>
      )}
    </div>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 dark:bg-slate-900 dark:border-slate-800">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-semibold">{children}</div>
    </div>
  );
}

function GranBtn({
  cur,
  k,
  set,
  children,
}: {
  cur: RevenueGranularity | "auto";
  k: RevenueGranularity | "auto";
  set: (v: RevenueGranularity | "auto") => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={() => set(k)}
      className={
        cur === k
          ? "rounded-md bg-slate-900 px-2 py-1 text-white dark:bg-white dark:text-slate-900"
          : "rounded-md px-2 py-1 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
      }
    >
      {children}
    </button>
  );
}

function ConfirmClear({
  deploymentName,
  count,
  pending,
  onConfirm,
  onCancel,
}: {
  deploymentName: string;
  count: number;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl dark:bg-slate-900">
        <h2 className="text-base font-semibold mb-1">
          Clear all devices from {deploymentName}?
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
          Removes {count} device{count === 1 ? "" : "s"} from this deployment by clearing
          their <code className="text-xs">tempo_deployment_xref</code> in Netbox. The deployment
          itself stays in Tempo — only the fleet-tracker mapping is removed.
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-2 text-sm rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={pending}
            className="px-3 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {pending ? "Clearing…" : `Clear ${count}`}
          </button>
        </div>
      </div>
    </div>
  );
}
